import { Component, OnInit, ElementRef, OnDestroy, Inject } from '@angular/core';
import { LoggerService } from '@wdpr/ra-angular-logger';
import { catchError, finalize, switchMap, takeUntil } from 'rxjs/operators';
import { Subject, throwError } from 'rxjs';
import get from 'lodash-es/get';
import isEmpty from 'lodash-es/isEmpty';
import findIndex from 'lodash-es/findIndex';
import '@wdpr/checkbox';
import '@wdpr/counter';

// Services
import { AuthenticationService } from '@finder/shared/services/authentication/authentication.service';
import { ConfigService } from '@finder/core/config.service';
import { DiningCalendarDaysService } from '@finder/shared/services/dining-calendar-days/dining-calendar-days.service';
import { FinderAnalyticsService } from '@finder/shared/services/finder-analytics/finder-analytics.service';
import { FinderApiConnectorService } from '@finder/shared/services/finder-api-connector/finder-api-connector.service';
import { KeyringService } from '@finder/shared/services/keyring/keyring.service';
import { MealPeriodScheduleService } from '@finder/shared/services/meal-period-schedule/meal-period-schedule.service';
import { ModalChildComponent } from '@finder/shared/components/modal/modal-child-component.interface';
import { ScheduleService } from '@finder/shared/services/schedule/schedule.service';
import { WindowRef } from '@finder/shared/services/window-ref/window-ref.service';

import formatScheduleTime from '@finder/shared/utils/string/formatScheduleTime';
import { ANALYTICS_CONSTANTS, LOCALES, MEAL_PERIOD_CONSTANTS } from '@finder/shared/constants/app.constants';
import { ModalComponent } from '@finder/shared/components//modal/modal.component';
import { GetDateStringProvider, GET_DATE_STRING } from '@finder/shared/utils/string/getDateString';
import { GetDaysBetweenDatesProvider, GET_DAYS_BETWEEN_DATES } from '@finder/shared/utils/date/getDaysBetweenDates';
import { GetDateObjectProvider, GET_DATE_OBJECT } from '@finder/shared/utils/date/getDateObject';

@Component({
    selector: 'finder-availability-modal',
    templateUrl: './availability-modal.component.html',
    styleUrls: ['./availability-modal.component.scss']
})
export class AvailabilityModalComponent implements OnInit, OnDestroy, ModalChildComponent {

    parentComponent: ModalComponent;
    data: any;
    maxCalendarDays = 0;
    timesOptions = [];
    restaurantOptions = [];
    partySizeLowerLimit = 1;
    partySizeUpperLimit;
    partySizeCountDefaultValue;
    partySizeSelected;
    selectedTime;
    selectedTimeObject;
    availabilityLoading = false;
    availabilityData: any = {};
    mealPeriodsOrder;
    productType = '';
    conflictCheckingInProgress = false;
    conflict: any;
    conflictTime: string;
    replaceReservationUrl: string;
    errorMessage: string;
    defaultAvailabilityError: string;
    participatingRestaurants: any;
    dinnerShowRestaurant: any;
    selectedRestaurant: any;
    selectedRestaurantOption: string;
    selectedRestaurantIndex = 0;
    unavailableMessageTitle: string;
    mealPeriodSchedules;
    disclaimerText: string;
    noSchedulesDisclaimerText: string;
    resortReservationDates: Array<string>;

    private destroy$ = new Subject<void>();
    private siteId;

    constructor(
        public finderApi: FinderApiConnectorService,
        public mealPeriodScheduleService: MealPeriodScheduleService,
        public scheduleService: ScheduleService,
        private authenticationService: AuthenticationService,
        public windowRef: WindowRef,
        public elementRef: ElementRef,
        public logger: LoggerService,
        private keyringService: KeyringService,
        private configService: ConfigService,
        private diningCalendarDaysService: DiningCalendarDaysService,
        private finderAnalyticsService: FinderAnalyticsService,
        @Inject(GET_DATE_STRING) private getDateString: GetDateStringProvider,
        @Inject(GET_DAYS_BETWEEN_DATES) private getDaysBetweenDates: GetDaysBetweenDatesProvider,
        @Inject(GET_DATE_OBJECT) private getDateObject: GetDateObjectProvider,
    ) { }

    ngOnInit() {
        this.siteId = this.configService.getValue('siteId');
        this.getMaxCalendarDays();
        this.partySizeUpperLimit = this.data.partySize.limit;
        this.partySizeCountDefaultValue =
            this.mealPeriodScheduleService.getPartySize() || this.data.partySize.defaultValue;
        this.partySizeSelected = this.partySizeCountDefaultValue;
        this.mealPeriodsOrder = MEAL_PERIOD_CONSTANTS.mealPeriodsOrder;
        this.processDiningData();
        this.mealPeriodScheduleService.scheduleUpdated
            .pipe(takeUntil(this.destroy$))
            .subscribe((data: any) => {
                this.processDiningData();
            });
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Process the Dining Data need it for the form.
     */
    processDiningData() {
        this.participatingRestaurants = get(this.mealPeriodScheduleService, 'mealPeriodsData.participatingRestaurants');
        this.dinnerShowRestaurant = get(this.mealPeriodScheduleService, 'mealPeriodsData.dinnerShowRestaurant');
        this.mealPeriodSchedules = get(this.mealPeriodScheduleService, 'mealPeriodsData.schedules') ||
            get(this.dinnerShowRestaurant, 'productSchedules');
        this.mealPeriodSchedules = isEmpty(this.mealPeriodSchedules) ? false : this.mealPeriodSchedules;
        this.noSchedulesDisclaimerText = this.data.noSchedulesDisclaimerText;
        this.disclaimerText = this.data.disclaimerText;
        if (this.dinnerShowRestaurant) {
            // Dinner Show case: will always have only one restaurant associated.
            // select the dinner show restaurant.
            this.selectedRestaurant = this.mealPeriodScheduleService.mealPeriodsData.dinnerShowRestaurant;
            this.timesOptions = this.mealPeriodScheduleService.getTimeOptions(this.selectedRestaurant);
            this.disclaimerText = `${this.data.paymentDisclaimerText} ${this.disclaimerText}`;
        } else if (this.participatingRestaurants) {
            // Dinning Event case: will have one or more participatingRestaurants.
            this.getRestaurantOptionsList();
            // if its the first time we load the modal select by default the first
            // restaurant in the list. If not select the last restaurant selected, that will
            // update the new schedules if the date changed.
            this.selectRestaurant(this.selectedRestaurantIndex);
            // get the restaurant times list for the times dropdown.
            this.timesOptions = this.mealPeriodScheduleService.getRestaurantTimes(this.selectedRestaurant);
        } else {
            // Generic Dining case
            this.timesOptions = this.mealPeriodScheduleService.getTimes();
        }
        // this product type will be need it as a param to request availability
        // values could be: dinner-show, dining-event, table-service
        this.productType = get(this.mealPeriodScheduleService, 'mealPeriodsData.productType');
        // select the default time from the dropdown.
        this.setTimeOption(this.timesOptions);
        this.selectSavedTime(this.timesOptions);
        // some dining products has special needs options,
        // validate the special needs option based on the time selected.
        this.validateSpecialNeedsRestriction();
    }

    /**
     * Process the times asociated with a selected restaurant.
     */
    getSelectedRestaurantTimes() {
        this.getRestaurantOptionsList();
        this.timesOptions = this.mealPeriodScheduleService.getRestaurantTimes(this.selectedRestaurant);
        this.setTimeOption(this.timesOptions);
        this.selectSavedTime(this.timesOptions);
        this.validateSpecialNeedsRestriction();
    }

    /**
     * Parse the restaurant list to create the options list for the dropdown.
     */
    getRestaurantOptionsList() {
        this.restaurantOptions = this.participatingRestaurants.map(restaurant => ({ key: restaurant.id, value: restaurant.name }));
    }

    selectSavedTime(options: Array<any>) {
        const savedTime = this.mealPeriodScheduleService.getSelectedTime();
        if (savedTime) {
            const index = findIndex(options, ['key', savedTime]);
            this.setTimeOption(options, index);
        }
    }

    /**
     * Select a time from dropdown.
     *
     * @param options the list of times.
     * @param index the index to select from the list
     */
    setTimeOption(options: Array<any>, index = 0): any {
        if (isEmpty(options)) {
            this.selectedTime = '';

            return;
        }
        if (index >= 0) {
            this.selectedTime = options[index].key;
            this.selectedTimeObject = options[index];
        }
    }

    /**
     * Action excecuted when the user selects a time from the dropdown.
     *
     * @param event
     */
    onSelectTime(event) {
        this.setTimeOption(this.timesOptions, event.detail.index);
    }

    onCounterChanged(event) {
        if (!get(event, 'detail.value')) {
            this.logger.error(
                'onCounterChanged event does not have details.value properties'
            );

            return;
        }
        this.partySizeSelected = event.detail.value;
    }

    onDateChanged(event) {
        this.resetVariables();
        this.timesOptions = [];
        // We only update schedules on scheduleService when it has an entityId
        // In the case of Dining events, entityId should be undefined
        if (this.scheduleService.entityId) {
            this.scheduleService.getSchedules(event);
        }
        this.mealPeriodScheduleService.getSchedules(event);
        // reset availability results
        this.availabilityData = {};
    }

    /**
     * Some dining products has special needs options,
     * and some of them has restriction days to ask in advance.
     * This method will validate those days restrictions to enable or
     * disable the options based on the date the user selected.
     */
    validateSpecialNeedsRestriction() {
        // if we don't have specialNeeds coming from the service don't continue.
        if (!get(this.selectedTimeObject, 'specialNeeds')) {
            return;
        }
        this.selectedTimeObject.specialNeeds.forEach(specialNeed => {
            specialNeed.disabled = false;
            if (specialNeed.daysRestriction) {
                // calculate if the user selected a day that meet the restriction.
                const daysRestrictionMilliseconds = specialNeed.daysRestriction * 1000 * 60 * 60 * 24;
                const allowedDateMiliseconds = new Date().getTime() + daysRestrictionMilliseconds;
                if (this.mealPeriodScheduleService.date.getTime() < allowedDateMiliseconds) {
                    specialNeed.disabled = true;
                }
            }
        });
    }

    onCalendarClose(event) {
        const inputElement = this.elementRef.nativeElement.querySelector('.calendar-button input');
        if (inputElement) {
            inputElement.focus();
        }
    }

    onSelectSpecialNeed(event, specialNeed) {
        if (specialNeed) {
            specialNeed.checked = event.detail.value;
        }
    }

    /**
     * When a restaurant is selected it gets the restaurant schedules times
     * and adds them to the times dropdown.
     *
     * @param event
     */
    selectRestaurantAction(event) {
        this.selectRestaurant(event.detail.index);
        this.getSelectedRestaurantTimes();
    }

    selectRestaurant(index) {
        // validate if the index is valid.
        if (index >= this.participatingRestaurants.length) {
            return;
        }
        this.selectedRestaurantIndex = index;
        this.selectedRestaurant = this.participatingRestaurants[index];
        this.selectedRestaurantOption = get(this.selectedRestaurant, 'id', this.selectedRestaurantOption);
    }

    formatTime(time) {
        return formatScheduleTime(time);
    }

    searchAvailability() {
        this.resetVariables();
        if (!this.selectedTime) {
            // if there is not time selected don't continue.
            return;
        }
        this.availabilityLoading = true;
        const searchByTime = this.selectedTime.indexOf(':') !== -1;
        const params: any = {};
        const specialNeeds = get(this.selectedTimeObject, 'specialNeeds', [])
            .filter(specialNeed => specialNeed.checked)
            .map(specialNeed => specialNeed.id);
        const specialNeedsParam = isEmpty(specialNeeds) ? '' : specialNeeds.join(',');

        // check if the user is searching by time of meal period
        if (searchByTime) {
            params.searchTime = this.selectedTime;
            this.mealPeriodScheduleService.saveSelectedTime(this.selectedTime);
        } else {
            params.mealPeriod = this.selectedTime;
            this.mealPeriodScheduleService.saveMealPeriod(this.selectedTime);
        }

        // validate if we have any restaurants selected
        if (this.participatingRestaurants && this.selectedRestaurant) {
            params.restaurantId = this.selectedRestaurant.id;
        }

        // check if we need to send any special needs options.
        if (specialNeedsParam) {
            params.specialNeeds = specialNeedsParam;
        }
        this.mealPeriodScheduleService.savePartySize(this.partySizeSelected);

        this.availSearchAnalyticsCall({
            linkId: ANALYTICS_CONSTANTS.availDetailSearchButtonLinkId,
            productQuantity: 1,
            event: 'dineSearchStart'
        });

        this.authenticationService.getSWID(false)
            .pipe(takeUntil(this.destroy$))
            .subscribe(swid => {
                this.getAvailabilityData(params, swid);
            });
    }

    getAvailabilityData(params, swid) {
        this.finderApi.getDiningAvailability(
            swid,
            this.siteId,
            this.mealPeriodScheduleService.entityId,
            this.productType,
            this.partySizeSelected,
            this.getDateString(this.mealPeriodScheduleService.date),
            params
        )
        .pipe(
            catchError(({ error }) => {
                this.logger.error('Error checking for dining availability:', error.message);

                return throwError(() => new Error(error));
            }),
            finalize(() => this.availabilityLoading = false),
            takeUntil(this.destroy$)
        )
        .subscribe({
            next: data => {
                this.availabilityData = data;
                const unavailable = this.availabilityData.unavailableMessage;
                let analyticsEvent = 'dineSearchSuccess';
                if (unavailable) {
                    const messageParams: any = {
                        searchRangeMiliseconds: this.availabilityData.searchRangeMiliseconds,
                        ...params
                    };
                    let template = unavailable.titleTemplate;
                    if (this.dinnerShowRestaurant) {
                        // get the message when we only have a single time (Dinner Show case).
                        messageParams.singleDate = true;
                        template = unavailable.singleDateTitleTemplate;
                    }
                    // get the message when we have a range of times.
                    this.unavailableMessageTitle = this.mealPeriodScheduleService
                        .getUnavailableTimesMessage(template, messageParams);

                    unavailable.links = [{
                        label: unavailable.linkText,
                        link: unavailable.link,
                        title: unavailable.linkText
                    }];

                    analyticsEvent = '';
                }

                this.availSearchAnalyticsCall({
                    linkId: ANALYTICS_CONSTANTS.availDetailSearchSuccess,
                    productQuantity: this.availabilityData?.offers?.length ?? 0,
                    event: analyticsEvent
                });
            },
            error: error => {
                // We have to catch the error and do something with it. This was causing errors during tests
                this.defaultAvailabilityError = get(this.mealPeriodScheduleService, 'mealPeriodsData.labels.defaultErrorMessage');
            }
        });
    }
    /**
     * Reserve a time offer for dining.
     *
     * @param offer the dining offer to reserve.
     */
    reserveTime(offer) {
        this.conflictCheckingInProgress = true;
        this.errorMessage = '';
        // check if the login state of the user is expired and ask for login again
        // if it's need it.
        this.authenticationService.validateLogin()
            .pipe(takeUntil(this.destroy$))
            .subscribe(swid => {
                if (swid) {
                    this.checkConflictingItems(swid, offer);
                }
            });
    }

    /**
     * Check conflicting items for the offer the user
     * wants to reserve.
     *
     * @param swid user swid
     * @param offer the offer returned by availability service.
     */
    checkConflictingItems(swid: string, offer: any) {
        // call reservation service to get
        // the conflicting items.
        this.finderApi.getConflictingItems(
            swid,
            this.getDateString(this.mealPeriodScheduleService.date),
            `${offer.time}:00`, // just add 00 seconds to the offer time
            get(this.mealPeriodScheduleService, 'mealPeriodsData.reservationType'),
            `${swid};type=swid`
        )
        .pipe(
            catchError(error => throwError(() => new Error(error))),
            takeUntil(this.destroy$)
        )
        .subscribe({
            next: data => {
                if (!data.conflict) {
                    // no conflicts, we can continue
                    // and redirect the user to the reservation flow.
                    this.windowRef.nativeWindow.location.href = offer.url;

                    return;
                }
                // we have a conflict here
                this.conflictCheckingInProgress = false;
                this.conflict = data.conflict;
                this.conflictTime = formatScheduleTime(this.conflict.time);
                const conflictingReservation = {
                    name: this.conflict.assetName,
                    time: this.conflictTime,
                    isOwner: this.conflict.isOwner,
                    cancelLink: this.conflict.cancelLink,
                };
                if (this.conflict.cancelEligible) {
                    this.replaceReservationUrl =
                        `${offer.url}&conflictingReservation=${JSON.stringify(conflictingReservation)}`;
                }
            },
            error: error => {
                this.conflictCheckingInProgress = false;
                // We have to catch and do something with the error. This was causing errors during test.
                this.errorMessage = error.message;
            }
        });
    }

    resetVariables() {
        this.conflict = false;
        this.replaceReservationUrl = '';
        this.conflictTime = '';
        this.conflictCheckingInProgress = false;
        this.errorMessage = '';
        this.defaultAvailabilityError = '';
        this.availabilityData = {};
        this.unavailableMessageTitle = '';
    }

    private getMaxCalendarDays() {
        // If 'getMaxCalDaysFromService' toggle is enabled in Vendo Calendar Config,
        // We need to use the calendar days avail API.
        if (this.data?.useCalendarAvailApi) {
            this.authenticationService.getSWID(false)
                .pipe(
                    switchMap((swid: string) =>
                        this.diningCalendarDaysService.getCalendarAvailDaysFromAvailApi(this.data.entityId, swid)),
                    takeUntil(this.destroy$)
                )
                .subscribe({
                    next: (serviceData: any) => this.maxCalendarDays = serviceData.maxDateNumber,
                    error: error => this.maxCalendarDays = this.data.maxDays
                });
            this.resortReservationDates = this.data.resortReservationDates;
        } else {
            // Additional days only apply on WDW
            if (this.siteId !== 'wdw') {
                this.maxCalendarDays = this.data.maxDays;

                return;
            }

            this.authenticationService.getSWID(false)
                .pipe(
                    switchMap((swid: string) => this.keyringService.getDiningAvailabilityCalendarDays(
                        swid,
                        this.data.maxDays,
                        this.data.maxAdditionalDays
                    )),
                    takeUntil(this.destroy$)
                )
                .subscribe({
                    next: (data: any) => this.maxCalendarDays = data.days,
                    error: error => this.maxCalendarDays = this.data.maxDays
                });
        }
    }

    private availSearchAnalyticsCall(analyticsData) {
        const selectedDateString = this.mealPeriodScheduleService.date.toLocaleDateString(
            LOCALES.en_US_normalized, {month: '2-digit', day: '2-digit', year: 'numeric'});
        this.finderAnalyticsService.updateModelAndTrack({
            linkId: `${this.siteId.toUpperCase()}_${analyticsData?.linkId}`,
            bookingSearchResort: this.mealPeriodScheduleService.entityId,
            events: analyticsData.event,
            products: [
                {
                    productType: 'dining',
                    productId: this.data?.productName ?? '',
                    quantity: analyticsData?.quantity ?? 1,
                    price: {total: 0},
                    bookingSearchArrivalDate: selectedDateString,
                    bookingSearchDaysBetweenSearchAndArrival:
                        this.getDaysBetweenDates(this.mealPeriodScheduleService.date, new Date())
                    ,
                    bookingSearchAdults: this.partySizeSelected
                }
            ]
        });
    }
}
